<template>
  <span :class="style.heading" v-if="description">{{ description }}</span>
  <div
    :class="[
      style.inputMediaFile,
      !hasImage ? style.noImg : null,
      isFile ? style.isFile : null
    ]"
  >
    <ButtonUploadImage
      v-if="hasImage"
      size="40"
      radius="8"
      :image="isVideoMedia ? '' : image"
      :icon="imageIcon"
      :disabled="disabled"
      iconTone="tertiary"
      @click="$emit('upload')"
    >
      <video
        v-if="isVideoMedia && mediaSrc"
        :class="style.mediaPreview"
        :src="mediaSrc"
        autoplay
        muted
        loop
        playsinline
        webkit-playsinline
        preload="metadata"
      />
    </ButtonUploadImage>

    <span v-if="text">
      {{ text }}
    </span>

    <input
      v-else
      :class="style.input"
      type="text"
      :value="modelValue"
      :placeholder="placeholder"
      :disabled="disabled"
      @input="updateValue"
    />

    <ActionMenu
      v-if="hasActions && !disabled"
      v-model="isOpenActionMenu"
      :actions="normalizedActions"
      :teleport="true"
      :closeOnScroll="true"
      :closeOnResize="true"
      placement="bottom-end"
      :offset="8"
      :popupStyle="computedPopupStyle"
    >
      <template #trigger="{isOpen, toggle}">
        <div data-floating-trigger @click.stop="toggle">
          <TabIcon
            size="28"
            :icon="icon"
            :class="!isOpen ? style.lastButton : null"
            :active="isOpen"
            variant="ghost"
          />
        </div>
      </template>
    </ActionMenu>

    <TabIcon
      v-else
      size="28"
      :icon="icon"
      :class="!isInfoTabActive ? style.lastButton : null"
      :disabled="disabled"
      :active="isInfoTabActive"
      variant="ghost"
      @click="showInfo"
    />
  </div>
</template>

<script lang="ts">
import {defineComponent, PropType} from "vue";
import style from "./InputFieldMedia.module.scss";
import moreIcon from "$images/icons/button/Name=More, Size=16, Tone=Default.svg";
import imageIcon from "$images/icons/button/Name=Image, Size=16, Tone=Default.svg";
import ActionMenu from "@/elements/UI/popup/ActionMenu/ActionMenu.vue";
import ButtonUploadImage from "@/elements/UI/button/ButtonUploadImage/ButtonUploadImage.vue";
import TabIcon from "@/elements/UI/tabs/TabIcon/TabIcon.vue";

export default defineComponent({
  name: "InputFieldMedia",

  components: {
    TabIcon,
    ButtonUploadImage,
    ActionMenu
  },

  emits: ["upload", "showInfo", "update:modelValue"],

  props: {
    modelValue: {
      type: String,
      default: ""
    },

    text: {
      type: String,
      default: ""
    },

    description: {
      type: String,
      default: ""
    },

    image: {
      type: String,
      default: ""
    },

    mediaType: {
      type: String as PropType<"image" | "video">,
      default: "image"
    },

    hasImage: {
      type: Boolean,
      default: true
    },

    icon: {
      type: String,
      default: moreIcon
    },

    placeholder: {
      type: String,
      default: ""
    },

    disabled: {
      type: Boolean,
      default: false
    },

    isFile: {
      type: Boolean,
      default: false
    },

    actions: {
      type: Array as PropType<ListActionItem[]>,
      default: (): ListActionItem[] => []
    }
  },

  data() {
    return {
      style,
      imageIcon,
      isOpenActionMenu: false,
      isInfoTabActive: false
    };
  },

  methods: {
    updateValue(event: Event): void {
      const input = event.target as HTMLInputElement;
      this.$emit("update:modelValue", input.value);
    },

    showInfo(): void {
      this.isInfoTabActive = true;
      this.$emit("showInfo");
    }
  },

  computed: {
    hasActions(): boolean {
      return this.actions.length > 0;
    },

    normalizedActions(): ListActionItem[] {
      return this.actions.map((action, index) => ({
        ...action,
        id: action.id || `${action.text}-${index}`,
        icon: action.icon || ""
      }));
    },

    computedPopupStyle(): Record<string, string | number> {
      return {
        padding: 0,
        boxSizing: "border-box"
      };
    },

    isVideoMedia(): boolean {
      return this.mediaType === "video";
    },

    mediaSrc(): string {
      const image = this.image.trim();

      if (!image) {
        return "";
      }

      const cssUrlMatch = image.match(/^url\((['"]?)(.*)\1\)$/);

      return cssUrlMatch?.[2] || image;
    }
  }
});
</script>
